import { User } from 'next-firebase-auth';
import { fetcherAndLog } from '@utils/fetcher';
import { userToken } from '@utils/firebase/initAuth';
import { getBaseUrl } from '@utils/general-utils';
import * as Sentry from '@sentry/nextjs';
import { CodingLanguage } from '@customtypes/parsedLesson';
import { ProjectFile } from './projectsRequests';

/**
 * Run files in the context of a specific lesson.
 *
 * @param trackId the track id
 * @param tutorialId the tutorial id
 * @param chapterId the chapter id
 * @param lessonId the lesson id
 * @param files the files to run
 * @param user the user
 * @param publishSetVersion the publish set version
 * @param isPreview the preview flag
 * @returns
 */
export async function runFilesInLessonContext({
  trackId,
  tutorialId,
  chapterId,
  lessonId,
  files,
  user,
  publishSetVersion,
  isPreview,
  suppressConsoleOutput,
  environmentVariables,
  signal,
}: {
  trackId: number;
  tutorialId: number;
  chapterId: number;
  lessonId: number;
  files: any[];
  user: User;
  publishSetVersion: number;
  isPreview: boolean;
  suppressConsoleOutput?: boolean;
  environmentVariables?: Record<string, string>;
  signal?: AbortSignal;
}) {
  const token = await userToken(user);

  const url = `${getBaseUrl(
    isPreview,
  )}/tracks/${trackId}/tutorials/${tutorialId}/chapters/${chapterId}/lessons/${lessonId}/runfiles?publishSetVersion=${publishSetVersion}${suppressConsoleOutput ? '&suppressConsoleOutput=true' : ''}`;

  const body = {
    files: files.map((file) => ({
      userContent: file.content,
      name: file.name,
    })),
    ...(environmentVariables && {
      environmentVariables,
    }),
  };

  const result = await fetcherAndLog(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!result.ok) {
    const { message } = await result.json();
    Sentry.captureException(
      new Error(
        `Could not run files - ${result.statusText} (${result.status})${
          message ? `: ${message}` : ''
        }`,
      ),
    );
    throw new Error(
      `Could not run files - ${result.statusText} (${result.status})${
        message ? `: ${message}` : ''
      }`,
    );
  }
  return result.json();
}

export async function runExpressFilesInLessonContext({
  trackId,
  tutorialId,
  chapterId,
  lessonId,
  files,
  user,
  isPreview,
  publishSetVersion,
  method,
  path,
  requestBody,
}: {
  trackId: number;
  tutorialId: number;
  chapterId: number;
  lessonId: number;
  files: any[];
  user: User;
  isPreview: boolean;
  publishSetVersion: number;
  method: string;
  path: string;
  requestBody?: any;
}): Promise<{
  hasPassed: boolean;
  error: string;
  processedErrorMessage: string;
  tests: { description: string; hasPassed: boolean }[];
  consoleOutput?: string;
  httpResponse: {
    statusCode: number;
    responseBody?: string;
    headers?: Record<string, string>;
  };
}> {
  const token = await userToken(user);

  const url = `${getBaseUrl(
    isPreview,
  )}/tracks/${trackId}/tutorials/${tutorialId}/chapters/${chapterId}/lessons/${lessonId}/runfiles/node?publishSetVersion=${publishSetVersion}`;

  const body = {
    method,
    path,
    files: files.map((file) => ({
      userContent: file.content,
      name: file.name,
    })),
    ...((method === 'POST' || method === 'PUT') && {
      body: requestBody,
    }),
  };

  const result = await fetcherAndLog(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
    body: JSON.stringify(body),
  });

  if (!result.ok) {
    const { message } = await result.json();
    Sentry.captureException(
      new Error(
        `Could not run files - ${result.statusText} (${result.status})${
          message ? `: ${message}` : ''
        }`,
      ),
    );
    throw new Error(
      `Could not run files - ${result.statusText} (${result.status})${
        message ? `: ${message}` : ''
      }`,
    );
  }
  return result.json();
}

/**
 * Run files without a specific lesson context.
 *
 * IMPORTANT NOTE: This endpoint does NOT support a preview flag.
 *
 * @param files the files to run
 * @param user the user
 * @returns
 */
export async function runFiles(
  files: { name: string; codeLanguage: CodingLanguage; content: string }[],
  user: User,
  signal?: AbortSignal,
) {
  const token = await userToken(user);
  const url = `${getBaseUrl(false)}/code/runfiles`;
  const body = {
    files,
  };
  const result = await fetcherAndLog(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!result.ok) {
    const { message } = await result.json();
    Sentry.captureException(
      new Error(
        `Could not run files - ${result.statusText} (${result.status})${
          message ? `: ${message}` : ''
        }`,
      ),
    );
    throw new Error(
      `Could not run files - ${result.statusText} (${result.status})${
        message ? `: ${message}` : ''
      }`,
    );
  }
  return result.json();
}

/**
 * Save and run files in a project context
 *
 * @param projectId the id of the project
 * @param files the files to run and save
 * @param user the user
 * @returns
 */
export async function runAndSaveProject(
  projectId: number,
  files: ProjectFile[],
  user: User,
  signal?: AbortSignal,
): Promise<{
  hostedFilesUrl?: string;
  consoleOutput: string;
  consoleOutputIsError: boolean;
}> {
  const token = await userToken(user);
  const url = `${getBaseUrl(false)}/code/${projectId}/runfiles`;
  const body = {
    files,
  };
  const result = await fetcherAndLog(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!result.ok) {
    const { message } = await result.json();
    Sentry.captureException(
      new Error(
        `Could not run project - ${result.statusText} (${result.status})${
          message ? `: ${message}` : ''
        }`,
      ),
    );
    throw new Error(
      `Could not run project - ${result.statusText} (${result.status})${
        message ? `: ${message}` : ''
      }`,
    );
  }
  return result.json();
}
